/**
 * @description Hostname classifiers for LynxPay platform vs SO tenant BFF hosts.
 * @dependencies none
 * @notes First-party Cloudflare Pages aliases (lynx-dashboard.pages.dev and
 *   branch previews) are platform LynxPay hosts, not Tier-3 custom domains.
 *   Keep LYNX_PAGES_PROJECTS in lock-step with backend/middleware/dynamicCors.js.
 */

const LYNX_BUILD_RESERVED_SLUGS = new Set(['admin', 'api', 'assets', 'lynx', 'staging', 'www']);

/** Hosts that always use platform (LynxPay) mode — never Tier-3 resolve. */
const PLATFORM_HOSTNAMES = new Set([
  'localhost',
  '127.0.0.1',
  'app.lynxpay.us',
  'app-staging.lynxpay.us',
  'www.lynxpay.us',
  'lynxpay.us',
]);

/**
 * LynxPay app hosts whose /api/* is same-origin via the Worker.
 * localhost, www, the apex, and *.pages.dev stay on REACT_APP_BACKEND_URL.
 */
const LYNXPAY_APP_BFF_HOSTS = new Set([
  'app.lynxpay.us',
  'app-staging.lynxpay.us',
]);

/**
 * First-party Cloudflare Pages projects. Only the project apex and the
 * `main.{project}.pages.dev` preview — not arbitrary branch aliases.
 * Do not treat all `*.pages.dev` as platform.
 */
const LYNX_PAGES_PROJECTS = [
  'lynx-dashboard.pages.dev',
  'lynx-clock.pages.dev',
  'lynx-admin.pages.dev',
  'lynx-portal.pages.dev',
];

/**
 * True for first-party Lynx Cloudflare Pages hosts (prod alias or main preview).
 * Rejects nested labels (`foo.bar.{project}`) and non-main branch previews.
 *
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isLynxPagesHostname(hostname) {
  const host = String(hostname || '').toLowerCase().split(':')[0];
  return LYNX_PAGES_PROJECTS.some((project) => {
    if (host === project) {
      return true;
    }
    if (!host.endsWith(`.${project}`)) {
      return false;
    }
    const alias = host.slice(0, -(project.length + 1));
    return alias === 'main';
  });
}

/**
 *
 * Canonical staging: {slug}.staging.lynx.build (LYN-27)
 * Production:         {slug}.lynx.build
 * CP-owned SO door:   {so}.{cp}.[staging.]lynx.build → SO slug (LYN-344)
 *
 * @param {string} hostname
 * @returns {string|null} SO slug or null for default / infrastructure hosts
 */
export function getLynxBuildTenantSlug(hostname) {
  const host = String(hostname || '').toLowerCase();
  const local = localOccupancyParts(host);
  if (local?.slug) {
    return local.slug;
  }

  if (!host.endsWith('.lynx.build')) {
    return null;
  }

  const stagingPrefixMatch = host.match(
    /^(app|admin)\.([a-z0-9]+(-[a-z0-9]+)*)\.staging\.lynx\.build$/
  );
  if (stagingPrefixMatch) {
    const slug = stagingPrefixMatch[2];
    return LYNX_BUILD_RESERVED_SLUGS.has(slug) ? null : slug;
  }

  const stagingSoUnderCpMatch = host.match(
    /^([a-z0-9]+(-[a-z0-9]+)*)\.([a-z0-9]+(-[a-z0-9]+)*)\.staging\.lynx\.build$/
  );
  if (stagingSoUnderCpMatch) {
    const soSlug = stagingSoUnderCpMatch[1];
    const cpSlug = stagingSoUnderCpMatch[3];
    return LYNX_BUILD_RESERVED_SLUGS.has(soSlug) || LYNX_BUILD_RESERVED_SLUGS.has(cpSlug)
      ? null
      : soSlug;
  }

  const stagingSubdomainMatch = host.match(/^([a-z0-9]+(-[a-z0-9]+)*)\.staging\.lynx\.build$/);
  if (stagingSubdomainMatch) {
    const slug = stagingSubdomainMatch[1];
    return LYNX_BUILD_RESERVED_SLUGS.has(slug) ? null : slug;
  }

  const subdomain = host.replace('.lynx.build', '');

  if (!subdomain || LYNX_BUILD_RESERVED_SLUGS.has(subdomain)) {
    return null;
  }

  // Production admin.{slug}.lynx.build is the CP admin factory (LYN-337).
  const prodAdminMatch = subdomain.match(/^admin\.([a-z0-9]+(-[a-z0-9]+)*)$/);
  if (prodAdminMatch) {
    const slug = prodAdminMatch[1];
    return LYNX_BUILD_RESERVED_SLUGS.has(slug) ? null : slug;
  }

  // Production app.{slug}.lynx.build is not a lookup host — app factory is {slug}.lynx.build.
  if (/^app\.[a-z0-9]+(-[a-z0-9]+)*$/.test(subdomain)) {
    return null;
  }

  const prodSoUnderCpMatch = subdomain.match(/^([a-z0-9]+(-[a-z0-9]+)*)\.([a-z0-9]+(-[a-z0-9]+)*)$/);
  if (prodSoUnderCpMatch) {
    const soSlug = prodSoUnderCpMatch[1];
    const cpSlug = prodSoUnderCpMatch[3];
    return LYNX_BUILD_RESERVED_SLUGS.has(soSlug) || LYNX_BUILD_RESERVED_SLUGS.has(cpSlug)
      ? null
      : soSlug;
  }

  return subdomain;
}

/**
 * True when the hostname is a Lynx.build staging factory host.
 *
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isLynxBuildStagingHostname(hostname) {
  return String(hostname || '').toLowerCase().split(':')[0].endsWith('.staging.lynx.build');
}

/**
 * Factory Channel Partner admin. host. Same family as existing admin. prefixes.
 * Staging: `admin.{slug}.staging.lynx.build`.
 * Production: `admin.{slug}.lynx.build`.
 * Do not use this for SO occupancy and do not change native app. origin.
 *
 * @param {string} slug
 * @param {{ staging?: boolean }} [options]
 * @returns {string|null}
 */
export function buildLynxBuildCpAdminHostname(slug, { staging } = {}) {
  const s = String(slug || '').toLowerCase();
  if (!s) {
    return null;
  }
  return staging
    ? `admin.${s}.staging.lynx.build`
    : `admin.${s}.lynx.build`;
}

/**
 * Admin. hostname for this Channel Partner occupancy.
 * Connected vanity → `admin.{cp_domain}`. Else factory admin.{slug}.
 * Returns null for SO / missing occupancy so app.{so} is never steered
 * to a parent CP admin host.
 *
 * @param {{
 *   kind?: string,
 *   slug?: string|null,
 *   connectedVanityDomain?: string|null
 * }|null|undefined} occupancy
 * @param {{ staging?: boolean }} [options]
 * @returns {string|null}
 */
export function buildChannelPartnerAdminHostname(occupancy, { staging } = {}) {
  if (!occupancy || occupancy.kind !== 'channel_partner') {
    return null;
  }
  const vanity = String(occupancy.connectedVanityDomain || '').trim().toLowerCase();
  if (vanity) {
    return `admin.${vanity}`;
  }
  return buildLynxBuildCpAdminHostname(occupancy.slug, { staging });
}

/**
 * Non-production occupancy on `*.localhost` (LYN-396).
 * `app.{slug}`, `admin.{slug}`, `{so}.{cp}`. Bare localhost is LynxPay.
 *
 * @param {string} [hostname]
 * @returns {{ prefix?: string, slug: string }|null}
 */
export function localOccupancyParts(hostname) {
  if (process.env.NODE_ENV === 'production') {
    return null;
  }
  const host = String(hostname || '').toLowerCase().split(':')[0];
  const prefixed = host.match(/^(app|admin)\.([a-z0-9]+(?:-[a-z0-9]+)*)\.localhost$/);
  if (prefixed) {
    if (LYNX_BUILD_RESERVED_SLUGS.has(prefixed[2])) {
      return null;
    }
    return { prefix: prefixed[1], slug: prefixed[2] };
  }
  const pair = host.match(/^([a-z0-9]+(?:-[a-z0-9]+)*)\.([a-z0-9]+(?:-[a-z0-9]+)*)\.localhost$/);
  if (!pair || pair[1] === 'portal') {
    return null;
  }
  if (LYNX_BUILD_RESERVED_SLUGS.has(pair[1]) || LYNX_BUILD_RESERVED_SLUGS.has(pair[2])) {
    return null;
  }
  return { slug: pair[1] };
}

/**
 * Channel Partner admin factory chrome host: admin.{slug}.staging.lynx.build
 * or production admin.{slug}.lynx.build (LYN-337). Bare localhost is LynxPay,
 * not CP admin. Non-production admin.{slug}.localhost is CP admin chrome.
 * Super Admin admin.lynx.build is not this.
 *
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isChannelPartnerAdminHost(hostname) {
  const host = String(hostname || '').toLowerCase().split(':')[0];
  if (host === 'localhost' || host === '127.0.0.1') {
    return false;
  }
  if (localOccupancyParts(host)?.prefix === 'admin') {
    return true;
  }
  const match = host.match(/^admin\.([a-z0-9]+(-[a-z0-9]+)*)\.(staging\.)?lynx\.build$/);
  return !!match && !LYNX_BUILD_RESERVED_SLUGS.has(match[1]);
}

/**
 * True for localhost / platform LynxPay hosts (not SO custom domains).
 * Includes first-party Pages dashboard/portal aliases used as staging/prod SPA origins.
 *
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isPlatformHostname(hostname) {
  const host = String(hostname || '').toLowerCase().split(':')[0];
  return PLATFORM_HOSTNAMES.has(host) || isLynxPagesHostname(host);
}

/**
 * True when the dashboard should use same-origin /api BFF (Worker proxy), not Railway.
 * Tier-2: *.lynx.build. Tier-3 custom hosts: same-origin (Worker only serves verified hosts).
 * LynxPay app.lynxpay.us and app-staging.lynxpay.us: same-origin.
 * Other platform hosts, localhost, and *.pages.dev: false.
 *
 * @param {string} [hostname=window.location.hostname]
 * @returns {boolean}
 */
export function usesTenantBffOrigin(
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) {
  const host = String(hostname || '').toLowerCase().split(':')[0];
  if (LYNXPAY_APP_BFF_HOSTS.has(host)) {
    return true;
  }
  if (localOccupancyParts(host)) {
    return true;
  }
  if (host.endsWith('.lynx.build')) {
    return true;
  }
  if (!host || isPlatformHostname(host)) {
    return false;
  }
  // Custom host SPA is only served by Worker after verified Tier-3 KV match.
  return true;
}

/**
 * API base URL for tenant-aware fetches on BFF hostnames (Tier 2 + Tier 3).
 *
 * @param {string} [hostname=window.location.hostname]
 * @param {string} [fallbackBackendUrl]
 * @returns {string}
 */
export function getTenantBackendBaseUrl(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  fallbackBackendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001'
) {
  if (usesTenantBffOrigin(hostname)) {
    if (typeof window !== 'undefined' && window.location.hostname === hostname) {
      return window.location.origin;
    }
    if (localOccupancyParts(hostname)) {
      const port = (typeof window !== 'undefined' && window.location.port) || '3000';
      return `http://${String(hostname).split(':')[0]}:${port}`;
    }
    return `https://${hostname}`;
  }
  return fallbackBackendUrl;
}
